import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Unlock, FileBarChart } from "lucide-react";

export default function DayEnd() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const [actual, setActual] = useState("");
  const [handover, setHandover] = useState("");
  const { data: preview, isLoading } = useQuery({
    queryKey: ["day-end-preview"],
    queryFn: async () => (await api.get("/day-end/preview")).data,
    refetchInterval: 15000,
  });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const sym = settings?.currency_symbol || "₹";

  const closeDay = async () => {
    try {
      await api.post("/day-end/close", {
        actual_cash: actual === "" ? undefined : Number(actual),
        handover_notes: handover || undefined,
        notes: handover || undefined,
      });
      toast.success("Day locked · POS payments paused");
      setActual("");
      setHandover("");
      qc.invalidateQueries({ queryKey: ["day-end-preview"] });
      qc.invalidateQueries({ queryKey: ["day-end-status"] });
      qc.invalidateQueries({ queryKey: ["cash-current"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Close failed"); }
  };
  const unlock = async () => {
    try {
      await api.post("/day-end/unlock", { date: preview?.date });
      toast.success("Day unlocked");
      qc.invalidateQueries({ queryKey: ["day-end-preview"] });
      qc.invalidateQueries({ queryKey: ["day-end-status"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Unlock failed"); }
  };

  if (isLoading || !preview) return <div className="p-8 text-neutral-400">Loading day-end…</div>;

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8" data-testid="day-end">
      <PageHeader title="Day-end / Z-report" subtitle={`Biller-wise close for ${preview.date}`}>
        {preview.locked ? (
          isAdmin ? <Button variant="outline" onClick={unlock} data-testid="day-unlock"><Unlock className="w-4 h-4 mr-1" /> Unlock day</Button> : <span className="text-sm text-neutral-500">Admin unlock required</span>
        ) : (
          <Button onClick={closeDay} className="bg-[var(--kul-primary)] text-white" data-testid="day-close">
            <Lock className="w-4 h-4 mr-1" /> Close & lock day
          </Button>
        )}
      </PageHeader>

      
      {preview.open_cash_session && !preview.locked && (
        <div className="kul-card p-4 mb-4 border border-amber-200 bg-amber-50 text-amber-900 text-sm" data-testid="day-open-cash">
          Cash session still open (opened {preview.open_cash_session.opened_at || "earlier"}). Close reconcile before day-end if you want a clean Z.
        </div>
      )}
<div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Paid orders" value={preview.paid_count} />
        <Stat label="Unpaid open" value={preview.unpaid_count} />
        <Stat label="Revenue" value={money(preview.revenue, sym)} />
        <Stat label="Tax" value={money(preview.tax, sym)} />
      </div>

      {preview.locked && (
        <div className="kul-card p-4 mb-4 border border-amber-200 bg-amber-50 text-amber-900 text-sm">
          Day locked by {preview.lock?.locked_by} at {preview.lock?.locked_at}. New payments are blocked until unlock.
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="kul-card p-5">
          <h3 className="font-display font-700 mb-3 flex items-center gap-2"><FileBarChart className="w-4 h-4" /> By biller</h3>
          <div className="space-y-2 text-sm">
            {(preview.by_biller || []).map((b) => (
              <div key={b.name} className="flex justify-between border-b border-neutral-100 py-2">
                <div>
                  <div className="font-medium">{b.name}</div>
                  <div className="text-xs text-neutral-500">{b.orders} orders · cash {money(b.cash, sym)} · card {money(b.card, sym)} · UPI {money(b.upi, sym)}</div>
                </div>
                <div className="font-semibold">{money(b.revenue, sym)}</div>
              </div>
            ))}
            {!preview.by_biller?.length && <p className="text-neutral-400">No paid sales yet today.</p>}
          </div>
        </div>
        <div className="kul-card p-5 space-y-4">
          <h3 className="font-display font-700">Payment mix</h3>
          {(preview.by_method || []).map((m) => (
            <div key={m.method} className="flex justify-between text-sm capitalize">
              <span>{m.method}</span><span>{money(m.amount, sym)}</span>
            </div>
          ))}
          {!preview.locked && (
            <>
              <div>
                <Label>Actual cash counted (optional)</Label>
                <Input type="number" value={actual} onChange={(e) => setActual(e.target.value)} placeholder="Matches drawer" />
              </div>
              <div>
                <Label>Shift handover notes</Label>
                <Input value={handover} onChange={(e) => setHandover(e.target.value)} placeholder="Pending deliveries, low stock, next shift…" data-testid="day-handover" />
              </div>
            </>
          )}
          {preview.locked && preview.lock?.handover_notes && (
            <div className="text-sm bg-neutral-50 rounded-lg p-3">
              <div className="text-xs uppercase text-neutral-400 mb-1">Handover</div>
              {preview.lock.handover_notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="kul-card p-4">
      <div className="text-xs uppercase tracking-wide text-neutral-400">{label}</div>
      <div className="font-display text-2xl font-800 mt-1">{value}</div>
    </div>
  );
}
